import { useEffect } from 'react';
import {
  Control,
  Controller,
  useFormContext,
  UseFormStateReturn,
  useWatch,
} from 'react-hook-form';
import { Subheader } from '../../../tailwindComponents/Subheader';
import { Input } from '../../../tailwindComponents/Input';
import { MultiSelect } from '../../../tailwindComponents/MultiSelect';
import { Select } from '../../../tailwindComponents/Select';
import { Textarea } from '../../../tailwindComponents/Textarea';
import { WarehouseFormValues } from '../types';
import {
  ADDITIONAL_SERVICES,
  CURRENCY,
  FULFILMENT_SERVICES,
  WAREHOUSE_OPERATIONS,
} from '../constants';
import { Checkbox } from '../../../tailwindComponents/Checkbox';

export function Services({
  control,
  formState,
}: {
  control: Control<WarehouseFormValues>;
  formState: UseFormStateReturn<WarehouseFormValues>;
}) {
  const { setValue } = useFormContext();
  const capacityUoM = useWatch({
    control,
    name: 'capacityUnitOfMeasure',
  });

  const haveOwnFleet = useWatch({
    control,
    name: 'haveOwnFleet',
  });

  const storageHelperText = () => {
    switch (capacityUoM.value) {
      case 'Pallets':
        return 'per pallet week';
      case 'Square Foot':
        return 'per sq.ft week';
      case 'Square Metre':
        return 'per sq.m week';
      default:
        return '';
    }
  };

  // Clear fleet values when haveOwnFleet becomes false
  useEffect(() => {
    if (!haveOwnFleet) {
      setValue('articsOver26t', '');
      setValue('rigidsUnder18t', '');
      setValue('ldvsUnder3_5t', '');
      setValue('vansUnder1_5t', '');
      setValue('rigidsUnder26t', '');
      setValue('rigidsUnder7_5t', '');
      setValue('specialOther', '');
    }
  }, [haveOwnFleet]);

  return (
    <>
      <Subheader subHeader='Services' />
      <div className='flex space-x-4'>
        <Controller
          name='warehouseOperations'
          control={control}
          render={({ field }) => (
            <MultiSelect
              label='Warehouse Operations'
              options={WAREHOUSE_OPERATIONS}
              selected={field.value || []}
              setSelected={field.onChange}
              isInvalid={!!formState.errors.warehouseOperations}
              validationMessage={
                formState.errors.warehouseOperations?.message as string
              }
            />
          )}
        />
        <Controller
          name='fulfilmentServices'
          control={control}
          render={({ field }) => (
            <MultiSelect
              label='Fulfilment Services'
              options={FULFILMENT_SERVICES}
              selected={field.value || []}
              setSelected={field.onChange}
              isInvalid={!!formState.errors.fulfilmentServices}
              validationMessage={
                formState.errors.fulfilmentServices?.message as string
              }
            />
          )}
        />
        <Controller
          name='additionalServices'
          control={control}
          render={({ field }) => (
            <MultiSelect
              label='Additional Services'
              options={ADDITIONAL_SERVICES}
              selected={field.value || []}
              setSelected={field.onChange}
              isInvalid={!!formState.errors.additionalServices}
              validationMessage={
                formState.errors.additionalServices?.message as string
              }
            />
          )}
        />
      </div>
      <Subheader subHeader='Charging rates' />
      <div className='grid grid-cols-[minmax(0,14rem)_10rem_8rem_minmax(0,14rem)_10rem] items-center gap-x-4 gap-y-3'>
        {/* Row 1 */}
        <label className='text-sm font-medium text-gray-900'>Currency</label>
        <Controller
          name='currency'
          control={control}
          render={({ field }) => (
            <Select options={CURRENCY} selected={field.value} setSelected={field.onChange} />
          )}
        />
        <div />
        <label className='text-sm font-medium text-gray-900'>Storage</label>
        <div>
          <Controller
            name='storage'
            control={control}
            render={({ field }) => (
              <Input {...field} name='storage' isInvalid={!!formState.errors.storage} validationMessage={formState.errors.storage?.message as string} />
            )}
          />
          <p className='mt-1 text-sm text-gray-500'>{storageHelperText()}</p>
        </div>

        {/* Row 2 */}
        <div><label className='text-sm font-medium text-gray-900'>Receipt and Putaway</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='receiptAndPutaway'
          control={control}
          render={({ field }) => (
            <Input {...field} name='receiptAndPutaway' isInvalid={!!formState.errors.receiptAndPutaway} validationMessage={formState.errors.receiptAndPutaway?.message as string} />
          )}
        />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Container Handling</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='containerHandling'
          control={control}
          render={({ field }) => (
            <Input {...field} name='containerHandling' isInvalid={!!formState.errors.containerHandling} validationMessage={formState.errors.containerHandling?.message as string} />
          )}
        />

        {/* Row 3 */}
        <div><label className='text-sm font-medium text-gray-900'>Pallet Picking and Dispatch</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='palletPickingAndDispatch'
          control={control}
          render={({ field }) => (
            <Input {...field} name='palletPickingAndDispatch' isInvalid={!!formState.errors.palletPickingAndDispatch} validationMessage={formState.errors.palletPickingAndDispatch?.message as string} />
          )}
        />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Pallet Returns</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='palletReturns'
          control={control}
          render={({ field }) => (
            <Input {...field} name='palletReturns' isInvalid={!!formState.errors.palletReturns} validationMessage={formState.errors.palletReturns?.message as string} />
          )}
        />

        {/* Row 4 */}
        <div><label className='text-sm font-medium text-gray-900'>Layer Picking and Dispatch</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='layerPickingAndDispatch'
          control={control}
          render={({ field }) => (
            <Input {...field} name='layerPickingAndDispatch' isInvalid={!!formState.errors.layerPickingAndDispatch} validationMessage={formState.errors.layerPickingAndDispatch?.message as string} />
          )}
        />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Provision of Pallet</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='provisionOfPallet'
          control={control}
          render={({ field }) => (
            <Input {...field} name='provisionOfPallet' isInvalid={!!formState.errors.provisionOfPallet} validationMessage={formState.errors.provisionOfPallet?.message as string} />
          )}
        />

        {/* Row 5 */}
        <div><label className='text-sm font-medium text-gray-900'>Case Picking and Dispatch</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='casePickingAndDispatch'
          control={control}
          render={({ field }) => (
            <Input {...field} name='casePickingAndDispatch' isInvalid={!!formState.errors.casePickingAndDispatch} validationMessage={formState.errors.casePickingAndDispatch?.message as string} />
          )}
        />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Case Returns</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='caseReturns'
          control={control}
          render={({ field }) => (
            <Input {...field} name='caseReturns' isInvalid={!!formState.errors.caseReturns} validationMessage={formState.errors.caseReturns?.message as string} />
          )}
        />

        {/* Row 6 */}
        <div><label className='text-sm font-medium text-gray-900'>Subsequent Case Picking and Dispatch</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='subsequentCasePickingAndDispatch'
          control={control}
          render={({ field }) => (
            <Input {...field} name='subsequentCasePickingAndDispatch' isInvalid={!!formState.errors.subsequentCasePickingAndDispatch} validationMessage={formState.errors.subsequentCasePickingAndDispatch?.message as string} />
          )}
        />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Item Returns</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='itemReturns'
          control={control}
          render={({ field }) => (
            <Input {...field} name='itemReturns' isInvalid={!!formState.errors.itemReturns} validationMessage={formState.errors.itemReturns?.message as string} />
          )}
        />

        {/* Row 7 */}
        <div><label className='text-sm font-medium text-gray-900'>Item Picking and Dispatch</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='itemPickingAndDispatch'
          control={control}
          render={({ field }) => (
            <Input {...field} name='itemPickingAndDispatch' isInvalid={!!formState.errors.itemPickingAndDispatch} validationMessage={formState.errors.itemPickingAndDispatch?.message as string} />
          )}
        />
        <div />
        <div>
          <div><label className='text-sm font-medium text-gray-900'>Auxiliary Services</label><p className='text-sm text-gray-400'>Optional</p></div>
        </div>
        <div>
          <Controller
            name='auxiliaryServices'
            control={control}
            render={({ field }) => (
              <Input {...field} name='auxiliaryServices' isInvalid={!!formState.errors.auxiliaryServices} validationMessage={formState.errors.auxiliaryServices?.message as string} />
            )}
          />
          <p className='mt-1 text-sm text-gray-500'>eg re-label, pallet rebuild, disposal</p>
        </div>

        {/* Row 8 */}
        <div><label className='text-sm font-medium text-gray-900'>Subsequent Item Picking and Dispatch</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='subsequentItemPickingAndDispatch'
          control={control}
          render={({ field }) => (
            <Input {...field} name='subsequentItemPickingAndDispatch' isInvalid={!!formState.errors.subsequentItemPickingAndDispatch} validationMessage={formState.errors.subsequentItemPickingAndDispatch?.message as string} />
          )}
        />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Demurrage Charges</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='demurrageCharges'
          control={control}
          render={({ field }) => (
            <Input {...field} name='demurrageCharges' isInvalid={!!formState.errors.demurrageCharges} validationMessage={formState.errors.demurrageCharges?.message as string} />
          )}
        />

        {/* Row 9 */}
        <div />
        <div />
        <div />
        <div>
          <div><label className='text-sm font-medium text-gray-900'>Surcharges, flat fee</label><p className='text-sm text-gray-400'>Optional</p></div>
        </div>
        <div>
          <Controller
            name='surcharges'
            control={control}
            render={({ field }) => (
              <Input {...field} name='surcharges' isInvalid={!!formState.errors.surcharges} validationMessage={formState.errors.surcharges?.message as string} />
            )}
          />
          <p className='mt-1 text-sm text-gray-500'>eg rush orders, out of hours, etc.</p>
        </div>
      </div>
      <Controller
        name='extraInfo'
        control={control}
        render={({ field }) => (
          <Textarea
            {...field}
            label='Extra Info'
            name='extraInfo'
            optional
            placeholder='Any additional details about your charging rates or services'
            isInvalid={!!formState.errors.extraInfo}
            validationMessage={formState.errors.extraInfo?.message as string}
          />
        )}
      />
      <Subheader subHeader='Fleet' />
      <div className='flex space-x-4'>
        <Controller
          name='haveOwnFleet'
          control={control}
          render={({ field }) => (
            <Checkbox
              label='I have my own fleet'
              checked={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
      {haveOwnFleet && (
        <div className='grid grid-cols-[minmax(0,14rem)_10rem_8rem_minmax(0,14rem)_10rem] items-center gap-x-4 gap-y-3'>
          {/* Row 1 */}
          <div><label className='text-sm font-medium text-gray-900'>Quantity of Artics &gt; 26t</label><p className='text-sm text-gray-400'>Optional</p></div>
          <Controller name='articsOver26t' control={control} render={({ field }) => (
            <Input {...field} name='articsOver26t' isInvalid={!!formState.errors.articsOver26t} validationMessage={formState.errors.articsOver26t?.message as string} />
          )} />
          <div />
          <div><label className='text-sm font-medium text-gray-900'>Quantity of Rigids &lt; 26t</label><p className='text-sm text-gray-400'>Optional</p></div>
          <Controller name='rigidsUnder26t' control={control} render={({ field }) => (
            <Input {...field} name='rigidsUnder26t' isInvalid={!!formState.errors.rigidsUnder26t} validationMessage={formState.errors.rigidsUnder26t?.message as string} />
          )} />

          {/* Row 2 */}
          <div><label className='text-sm font-medium text-gray-900'>Quantity of Rigids &lt; 18t</label><p className='text-sm text-gray-400'>Optional</p></div>
          <Controller name='rigidsUnder18t' control={control} render={({ field }) => (
            <Input {...field} name='rigidsUnder18t' isInvalid={!!formState.errors.rigidsUnder18t} validationMessage={formState.errors.rigidsUnder18t?.message as string} />
          )} />
          <div />
          <div><label className='text-sm font-medium text-gray-900'>Quantity of Rigids &lt; 7.5t</label><p className='text-sm text-gray-400'>Optional</p></div>
          <Controller name='rigidsUnder7_5t' control={control} render={({ field }) => (
            <Input {...field} name='rigidsUnder7_5t' isInvalid={!!formState.errors.rigidsUnder7_5t} validationMessage={formState.errors.rigidsUnder7_5t?.message as string} />
          )} />

          {/* Row 3 */}
          <div><label className='text-sm font-medium text-gray-900'>Quantity of LDVs &lt; 3.5t</label><p className='text-sm text-gray-400'>Optional</p></div>
          <Controller name='ldvsUnder3_5t' control={control} render={({ field }) => (
            <Input {...field} name='ldvsUnder3_5t' isInvalid={!!formState.errors.ldvsUnder3_5t} validationMessage={formState.errors.ldvsUnder3_5t?.message as string} />
          )} />
          <div />
          <div><label className='text-sm font-medium text-gray-900'>Quantity of Special/Other</label><p className='text-sm text-gray-400'>Optional</p></div>
          <Controller name='specialOther' control={control} render={({ field }) => (
            <Input {...field} name='specialOther' isInvalid={!!formState.errors.specialOther} validationMessage={formState.errors.specialOther?.message as string} />
          )} />

          {/* Row 4 */}
          <div><label className='text-sm font-medium text-gray-900'>Quantity of Vans &lt; 1.5t</label><p className='text-sm text-gray-400'>Optional</p></div>
          <Controller name='vansUnder1_5t' control={control} render={({ field }) => (
            <Input {...field} name='vansUnder1_5t' isInvalid={!!formState.errors.vansUnder1_5t} validationMessage={formState.errors.vansUnder1_5t?.message as string} />
          )} />
          <div />
          <div />
          <div />
        </div>
      )}
    </>
  );
}
